import {STANFORD_LOGO_ASSET_SRC} from 'branding/stanfordLogoSrc';
import {Loading} from 'components/loading/Loading';
import {useAppOverrides} from 'contexts/AppOverridesContext';
import {isFoothillCourse} from 'contexts/CourseContext';
import {buildThemeColorStyles} from 'course/contexts/themeUtils';
import {
  getCourseThemeDocumentRef,
  type CourseThemeDocument,
} from 'course/models/courseThemeDocument';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import { useCourseId, useCourseIdWithoutFallback } from 'hooks/router/useUrlParams';
import {createContext, useContext, useMemo, type ReactElement} from 'react';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import {Helmet} from 'react-helmet';
import type {CourseId} from 'types/common';

interface ThemeContextData extends Required<CourseThemeDocument> {
  isLoading: boolean;
  error: Error | null;
}

/**
 * "Code in Place" Purple. Used to override the public course's default
 * Cardinal Red for courses that do not have their theme set.
 */
const DEFAULT_NAVBAR_COLOR = '#5e40cb';
const DEFAULT_BUTTON_COLOR = '#2a61d1';
export const DEFAULT_COURSE_THEME_DATA: ThemeContextData = {
  isLoading: true,
  error: null,
  navbarColor: DEFAULT_NAVBAR_COLOR,
  navbarLogoImageUrl: '',
  navbarLogoText: 'Stanford',
  mainBodyFontFamily: '',
  mainBodyFontWeight: 0,
  h1FontFamily: '',
  h1FontWeight: 0,
  h2FontFamily: '',
  h2FontWeight: 0,
  h3FontFamily: '',
  h3FontWeight: 0,
  h4FontFamily: '',
  h4FontWeight: 0,
  h5FontFamily: '',
  h5FontWeight: 0,
  codeFontFamily: '',
  codeFontWeight: 0,
  remoteFontUrls: [],
  loginPageTitle: '',
  loginPageImageUrl: '',
  buttonColorPrimary: DEFAULT_BUTTON_COLOR,
  splashPageLogoUrl: STANFORD_LOGO_ASSET_SRC,
  bodyBackgroundColor: '',
  bodyTextColor: '',
  surfaceColor: '',
  surfaceTextColor: '',
  linkColor: '',
  contactEmail: 'codeinplace@stanford.edu',
  brandName: 'Code in Place',
  institutionName: 'Stanford University',
};

const CourseThemeContext = createContext<ThemeContextData>(DEFAULT_COURSE_THEME_DATA);

/**
 * The theme documents a {@link CourseThemeProvider} has finished loading,
 * republished for providers nested beneath it. A nested provider for the same
 * course reuses these instead of reading both documents again; it still
 * applies its own `allowDarkScheme`. `null` where no provider is above.
 */
interface LoadedThemeDocuments {
  /** The course the documents belong to; the nested provider must match it. */
  courseId: CourseId | undefined;
  /** `course/{courseId}/docs/theme`, undefined when the course has none. */
  currentCourseThemeDocument: CourseThemeDocument | undefined;
  /** `course/public/docs/theme`, the fallback every course inherits. */
  publicCourseThemeDocument: CourseThemeDocument | undefined;
  /** The first read error, if either read failed. */
  error: Error | undefined;
}
const LoadedThemeDocumentsContext = createContext<LoadedThemeDocuments | null>(
  null,
);

export const CourseThemeProvider = ({
  courseId,
  children,
}: {
  courseId?: CourseId;
  children: React.ReactNode;
}) => {
  const currentCourseId = useCourseIdWithoutFallback();
  const effectiveCourseId = courseId ?? currentCourseId;
  // A deploy wrapper (e.g. PAI) can supply a course theme synchronously via
  // AppOverrides, so its look and feel applies with no Firestore round-trip.
  // It sits beneath any per-course Firestore theme in the merge below, so a
  // specific course can still override individual fields.
  const {courseTheme: appOverrideCourseTheme} = useAppOverrides();
  // Courseless routes (the sign-in shell and every other route with no course
  // segment) resolve to an empty course id, which would build the odd-segment
  // path `course/` and make `doc()` throw — crashing the whole subtree to the
  // ErrorBoundary. There is no per-course theme to read there; the public theme
  // read below is the whole answer. See
  // [#3832](https://github.com/CodeinPlace/cip/issues/3832).
  const inheritedDocuments = useContext(LoadedThemeDocumentsContext);
  const reusesInheritedDocuments =
    inheritedDocuments !== null &&
    inheritedDocuments.courseId === effectiveCourseId;
  const currentCourseThemeDocumentRef =
    effectiveCourseId && !reusesInheritedDocuments
      ? getCourseThemeDocumentRef(effectiveCourseId)
      : null;
  const publicCourseThemeDocumentRef = reusesInheritedDocuments
    ? null
    : getCourseThemeDocumentRef('public');

  const [
    fetchedCurrentCourseThemeDocument,
    fetchedCurrentCourseThemeDocumentLoading,
    fetchedCurrentCourseThemeDocumentError,
  ] = useDocumentDataOnce(currentCourseThemeDocumentRef);
  const [
    fetchedPublicCourseThemeDocument,
    fetchedPublicCourseThemeDocumentLoading,
    fetchedPublicCourseThemeDocumentError,
  ] = useDocumentDataOnce(publicCourseThemeDocumentRef);

  // A null ref still reports one loading render before the hook settles, so
  // the reuse branch must not consult the hook flags at all.
  const currentCourseThemeDocument = reusesInheritedDocuments
    ? inheritedDocuments.currentCourseThemeDocument
    : fetchedCurrentCourseThemeDocument;
  const publicCourseThemeDocument = reusesInheritedDocuments
    ? inheritedDocuments.publicCourseThemeDocument
    : fetchedPublicCourseThemeDocument;
  const themeDocumentsLoading = reusesInheritedDocuments
    ? false
    : fetchedCurrentCourseThemeDocumentLoading ||
      fetchedPublicCourseThemeDocumentLoading;
  const themeDocumentsError = reusesInheritedDocuments
    ? inheritedDocuments.error
    : fetchedCurrentCourseThemeDocumentError ||
      fetchedPublicCourseThemeDocumentError;
  const loadedThemeDocuments = useMemo<LoadedThemeDocuments>(
    () => ({
      courseId: effectiveCourseId,
      currentCourseThemeDocument,
      publicCourseThemeDocument,
      error: themeDocumentsError,
    }),
    [
      effectiveCourseId,
      currentCourseThemeDocument,
      publicCourseThemeDocument,
      themeDocumentsError,
    ],
  );

  const theme: ThemeContextData = {
    // Use the current course's theme data if it exists, fall back to public
    // theme data if it doesn't. If neither exist (this should never happen),
    // use the hardcoded default theme data.
    ...DEFAULT_COURSE_THEME_DATA,
    ...publicCourseThemeDocument,
    // Override the public course's default navbar color with the hardcoded
    // default for courses that do not have their theme set.
    ...{
      navbarColor: DEFAULT_NAVBAR_COLOR,
    },
    // Deploy-wrapper theme (AppOverrides): applies to every course in the
    // deploy, but a per-course Firestore theme below can still override it.
    ...appOverrideCourseTheme,
    // Override the default Stanford logo for Foothill courses that do not have
    // their theme set.
    ...currentCourseThemeDocument,
    ...(isFoothillCourse(effectiveCourseId)
      ? {
          navbarLogoText: 'Stanford & Foothill',
        }
      : {}),
    isLoading: themeDocumentsLoading,
    error: themeDocumentsError,
  };

  const areAnyFontsChanged =
    theme.mainBodyFontFamily ||
    theme.h1FontFamily ||
    theme.h2FontFamily ||
    theme.h3FontFamily ||
    theme.h4FontFamily ||
    theme.h5FontFamily ||
    theme.codeFontFamily;

  const remotelyLoadedFontStyles = useMemo(() => {
    return getRemotelyLoadedFontStyles(theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [theme?.remoteFontUrls]);
  const fontFamilies = useMemo(() => {
    return getFontFamilies(theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [
    theme.mainBodyFontFamily,
    theme.h1FontFamily,
    theme.h2FontFamily,
    theme.h3FontFamily,
    theme.h4FontFamily,
    theme.h5FontFamily,
    theme.codeFontFamily,
  ]);

  // Full color theming: emit the generic `--course-*` custom properties and
  // Bootstrap bridge vars (plus a `body` rule) from the theme's color fields.
  // A course with no color fields set produces an empty string, so its page is
  // unchanged. See `buildThemeColorStyles` for the field → variable mapping.
  const colorStyles = useMemo(() => {
    return buildThemeColorStyles(theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [
    theme.bodyBackgroundColor,
    theme.bodyTextColor,
    theme.surfaceColor,
    theme.surfaceTextColor,
    theme.linkColor,
  ]);

  if (theme.isLoading) {
    return <Loading />;
  }
  return (
    <CourseThemeContext.Provider value={theme}>
      <LoadedThemeDocumentsContext.Provider value={loadedThemeDocuments}>
        {(areAnyFontsChanged || colorStyles) && (
          <Helmet>
            {remotelyLoadedFontStyles}
            {areAnyFontsChanged && <style>{fontFamilies}</style>}
            {colorStyles && <style>{colorStyles}</style>}
          </Helmet>
        )}
        {children}
      </LoadedThemeDocumentsContext.Provider>
    </CourseThemeContext.Provider>
  );
};

const getFontFamilies = (theme: ThemeContextData): string => {
  let fontStyles = '';
  if (theme.mainBodyFontFamily) {
    fontStyles += `
      body {
        font-family: "${theme.mainBodyFontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.mainBodyFontWeight ? `font-weight: ${theme.mainBodyFontWeight};` : ''}
      }
    `;
  }
  if (theme.h1FontFamily) {
    fontStyles += `
      h1 {
        font-family: "${theme.h1FontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.h1FontWeight ? `font-weight: ${theme.h1FontWeight};` : ''}
      }
    `;
  }
  if (theme.h2FontFamily) {
    fontStyles += `
      h2 {
        font-family: "${theme.h2FontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.h2FontWeight ? `font-weight: ${theme.h2FontWeight};` : ''}
      }
    `;
  }
  if (theme.h3FontFamily) {
    fontStyles += `
      h3 {
        font-family: "${theme.h3FontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.h3FontWeight ? `font-weight: ${theme.h3FontWeight};` : ''}
      }
    `;
  }
  if (theme.h4FontFamily) {
    fontStyles += `
      h4 {
        font-family: "${theme.h4FontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.h4FontWeight ? `font-weight: ${theme.h4FontWeight};` : ''}
      }
    `;
  }
  if (theme.h5FontFamily) {
    fontStyles += `
      h5 {
        font-family: "${theme.h5FontFamily}", Nunito, Helvetica, sans-serif;
        ${theme.h5FontWeight ? `font-weight: ${theme.h5FontWeight};` : ''}
      }
    `;
  }
  if (theme.codeFontFamily) {
    fontStyles += `
      code {
        font-family: "${theme.codeFontFamily}", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace !important;
        ${theme.codeFontWeight ? `font-weight: ${theme.codeFontWeight};` : ''}
      }
      
      pre {
        font-family: "${theme.codeFontFamily}", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace !important;
        ${theme.codeFontWeight ? `font-weight: ${theme.codeFontWeight};` : ''}
      }
      
      .refCode > * code {
        font-family: "${theme.codeFontFamily}", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace !important;
        ${theme.codeFontWeight ? `font-weight: ${theme.codeFontWeight};` : ''}
      }

      .refCode > code {
        font-family: "${theme.codeFontFamily}", Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace !important;
        ${theme.codeFontWeight ? `font-weight: ${theme.codeFontWeight};` : ''}
      }
    `;
  }
  return fontStyles;
};

const getRemotelyLoadedFontStyles = (
  theme: ThemeContextData,
): ReactElement[] => {
  return (
    theme.remoteFontUrls?.map(url => (
      <link key={url} rel="stylesheet" href={url} />
    )) ?? []
  );
};

export const useCourseTheme = (): ThemeContextData => {
  const theme = useContext(CourseThemeContext);
  if (!theme) {
    throw new Error('useCourseTheme must be used within a CourseThemeProvider');
  }
  return theme;
};
